import api from 'services/api';
import { REJECT, array, nullable, num, object, str, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

/**
 * A row of nap-backend `GET /python_libraries/:id/node_definitions` — the node definitions
 * currently linked to this library, through the `node_definition_python_libraries` junction.
 *
 * Its own type rather than a reuse of `node_definitions`' row types: this projection is built by
 * the python_libraries route (`SELECT nd.node_definition_id, nd.name, nd.type, nd.description`
 * over the join), and carries neither the zone nor any of the list columns.
 *
 * Note the asymmetry with the sibling PUT: the read returns full rows, the write takes bare ids.
 */
export interface LinkedNodeDefinition {
  readonly node_definition_id: number;
  readonly name: string;
  readonly type: string;
  readonly description: string | null;
}

const rowSchema = object<LinkedNodeDefinition>({
  node_definition_id: num(REJECT),
  name: str(),
  type: str(),
  description: nullable(str()),
});

/**
 * GET `/python_libraries/:id/node_definitions`. Returns the linked definitions, name-ascending.
 * Rejects on transport error.
 *
 * Answers with a `Received` rather than collapsing with `acceptDegraded`, which is a third case the scope
 * table in ../../README.md does not name: **this read's value becomes a write body.** The sibling
 * PUT replaces the whole set, so the caller is about to change state it believes this response
 * describes — the same reason writes and by-id editor reads carry the union.
 *
 * That matters most for the outcome that still looks like success. A row whose identity drifted is
 * DROPPED (`[*]` is a drop-row directive, and a fail-per-row one is not expressible), and a dropped
 * row is normally "usable but shorter". Here it is not: shorter means the caller writes back a set
 * with a link missing, and the backend deletes that link. So a caller that writes this set back
 * must treat a `dropped` problem as a failed read — `EditPythonLibraryModal` does.
 */
export const getPythonLibraryNodeDefinitions = async (id: number): Promise<Received<readonly LinkedNodeDefinition[]>> => {
  const res = await api.get(`/python_libraries/${id}/node_definitions`);
  return readResponse(res.data, {
    schema: array(rowSchema),
    source: 'getPythonLibraryNodeDefinitions',
  });
};
